import axios from "axios";

export async function createAxiosInstance(token: string) {
  let bearerToken: string;
  if (token) {
    bearerToken = "bearer " + token;
  } else {
    bearerToken = "";
  }
  const axiosInstance = axios.create({
    baseURL: process.env.REACT_APP_BASE_URL_LOCAL,
    validateStatus: function (status) {
      // Only treat 2xx as success - we'll check for redirects in the interceptor
      return status >= 200 && status < 300;
    }
  });

  axiosInstance.defaults.headers.common["Authorization"] = bearerToken;
  axiosInstance.defaults.headers.common["Content-Type"] = "application/json;charset=UTF-8";
  axiosInstance.defaults.headers.common["accept"] = "application/json, text/plain, */*";

  // Request interceptor to track the original request URL
  axiosInstance.interceptors.request.use(
    function (config) {
      // Store the original request URL to detect redirects later
      (config as any).__originalUrl = config.url;
      (config as any).__originalBaseURL = config.baseURL;
      return config;
    },
    function (error) {
      return Promise.reject(error);
    }
  );

  // Response interceptor to detect redirects
  axiosInstance.interceptors.response.use(
    function (response) {
      // Check if we were redirected to Keycloak
      const originalUrl = (response.config as any).__originalUrl;
      const originalBaseURL = (response.config as any).__originalBaseURL || process.env.REACT_APP_BASE_URL_LOCAL;
      const requestUrl = originalBaseURL ? `${originalBaseURL}${originalUrl}` : originalUrl;
      const responseUrl = response.request?.responseURL || response.config?.url;

      // Check if response URL is from Keycloak (different domain) or if response is HTML instead of JSON
      const contentType = response.headers['content-type'] || '';
      const isKeycloakRedirect = responseUrl && responseUrl.includes('/auth/realms/');

      // Also check if response data looks like HTML (Keycloak login page)
      const responseData = response.data;
      const isHtmlResponse = typeof responseData === 'string' && (
        responseData.includes('<!DOCTYPE html') ||
        responseData.includes('<html') ||
        responseData.includes('keycloak') ||
        responseData.includes('Sign in')
      );

      if (isKeycloakRedirect || isHtmlResponse || (responseUrl && requestUrl && responseUrl !== requestUrl && !responseUrl.startsWith(originalBaseURL || ''))) {
        // We were redirected to Keycloak - session expired
        console.warn('Detected redirect to Keycloak - session expired', { requestUrl, responseUrl, contentType, isHtmlResponse });
        localStorage.removeItem("userAuthorized");
        // Set flag to trigger session expired modal
        localStorage.setItem("api_401_detected", "true");
        // Dispatch a custom event to notify components
        window.dispatchEvent(new CustomEvent('api401'));
        return Promise.reject({
          response: { status: 401, data: { error: 'Session expired' } },
          message: 'Session expired - redirected to auth server'
        });
      }

      if (response.status === 200) localStorage.setItem("userAuthorized", true.toString());
      return response;
    },
    function (error) {
      // Handle network errors (CORS, redirects, etc.)
      if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
        // This often happens when API redirects to Keycloak (CORS issue)
        // or when browser blocks the redirect due to CORS
        console.warn('Network error - likely due to expired session or CORS redirect:', error.message);
        localStorage.removeItem("userAuthorized");
        // Set flag to trigger session expired modal
        localStorage.setItem("api_401_detected", "true");
        // Dispatch a custom event to notify components
        window.dispatchEvent(new CustomEvent('api401'));
        // Reject with a 401-like error so components can handle it
        return Promise.reject({
          response: { status: 401, data: { error: 'Session expired' } },
          message: 'Network error - session likely expired'
        });
      }

      // Check if error response is from Keycloak
      const responseUrl = error.response?.request?.responseURL || error.config?.url;
      const isKeycloakError = responseUrl && responseUrl.includes('/auth/realms/');

      if (isKeycloakError) {
        console.warn('Error response from Keycloak - session expired');
        localStorage.removeItem("userAuthorized");
        // Set flag to trigger session expired modal
        localStorage.setItem("api_401_detected", "true");
        // Dispatch a custom event to notify components
        window.dispatchEvent(new CustomEvent('api401'));
        return Promise.reject({
          response: { status: 401, data: { error: 'Session expired' } },
          message: 'Session expired - redirected to auth server'
        });
      }

      // Handle redirect errors (3xx status codes)
      if (error.response && error.response.status >= 300 && error.response.status < 400) {
        console.warn('API returned redirect status - session expired');
        localStorage.removeItem("userAuthorized");
        // Set flag to trigger session expired modal
        localStorage.setItem("api_401_detected", "true");
        // Dispatch a custom event to notify components
        window.dispatchEvent(new CustomEvent('api401'));
        // Convert to 401 error
        return Promise.reject({
          response: { status: 401, data: { error: 'Session expired' } },
          message: 'Session expired - redirect to auth server'
        });
      }

      if (error.response && error.response.status === 401) {
        // Handle unauthorized error
        console.error('Unauthorized error:', error);
        localStorage.removeItem("userAuthorized");
        // Set flag to trigger session expired modal
        localStorage.setItem("api_401_detected", "true");
        // Dispatch a custom event to notify components
        window.dispatchEvent(new CustomEvent('api401'));
      }
      return Promise.reject(error);
    }
  );
  return axiosInstance;
}

export async function handleResponse(response: any) {
  if (response.ok) {
    localStorage.setItem("userAuthorized", true.toString());
    return response.json();
  }
  if (response.status === 400) {
    // So, a server-side validation error occurred.
    // Server side validation returns a string error message, so parse as text instead of json.
    const errorText = await response.text();
    throw new Error(errorText);
  }
  if (response.status === 401) {
    // Handle unauthorized error
    console.error('Unauthorized error:', response);
    localStorage.removeItem("userAuthorized");
    // Optionally, you can trigger a logout or refresh token process here
  }
  throw new Error("Network response was not ok.");
}

// In a real app, would likely call an error logging service.
export function handleError(error: any) {
  // Don't log or throw 401 errors - they're handled by the interceptor and modal
  if (error?.response?.status === 401) {
    // Silently handle 401s - the modal is already shown
    return;
  }

  // Format error message properly
  let errorMessage = "API call failed.";
  if (error?.message) {
    errorMessage += " " + error.message;
  } else if (error?.response?.data) {
    if (typeof error.response.data === 'string') {
      errorMessage += " " + error.response.data;
    } else if (error.response.data.error) {
      errorMessage += " " + error.response.data.error;
    } else {
      errorMessage += " " + JSON.stringify(error.response.data);
    }
  } else if (typeof error === 'string') {
    errorMessage += " " + error;
  } else if (error?.toString && error.toString() !== '[object Object]') {
    errorMessage += " " + error.toString();
  }

  // eslint-disable-next-line no-console
  console.error(errorMessage, error);
  throw error;
}

export function getQueryString(query: any) {
  let queryString = "";
  let str = [];

  for (var p in query) {
    var type = typeof query[p];
    if (type === "object") {
      var arr = query[p];
      for (var v in arr) {
        if (arr[v] !== null)
          str.push(encodeURIComponent(p) + "=" + encodeURIComponent(arr[v]));
      }
    } else {
      if (query[p] !== null)
        str.push(encodeURIComponent(p) + "=" + encodeURIComponent(query[p]));
    }
  }
  queryString = str.join("&");
  return queryString;

}

export const page_size = 20; // Pagination size for table lists (paginated, user-scrollable)
export const filter_page_size = 200; // Pagination size for filter dropdowns (preloaded upfront to reduce API requests)